// v3.0.0-rc.8t — LiveDashboardPage : dashboard commerçant pendant un live
// Inspiré de la capture 1 (QR au centre, CA temps réel, stats, achats en direct).
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";

function fmtFcfa(n) { return Number(n || 0).toLocaleString("fr-FR"); }
function fmtDuration(s) {
  s = Math.max(0, Number(s) || 0);
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  return `${String(h).padStart(2,"0")}:${String(m).padStart(2,"0")}:${String(sec).padStart(2,"0")}`;
}

export default function LiveDashboardPage() {
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [products, setProducts] = useState([]);
  const [pinnedId, setPinnedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);
  // v3.0.0-rc.8v — Champs déclarés par le commerçant au démarrage (aucun scraping — juste du contexte).
  const [platform, setPlatform] = useState("tiktok");
  const [liveUrl, setLiveUrl] = useState("");
  const [liveTitle, setLiveTitle] = useState("");
  const [debrief, setDebrief] = useState(null);
  const esRef = useRef(null);
  // v3.0.0-rc.8v — Ref pour exposer fetchStats à LiveOrderRow (via callback onDispatchDone).
  const fetchStatsFromRef = useRef(null);

  const _auth = () => ({ Authorization: `Bearer ${localStorage.getItem("civ_user_token") || ""}` });

  // v4.5.0 — Ka-ching : son + vibration + flash visuel à chaque nouvelle vente.
  const prevOrdersRef = useRef(null);
  const [saleFlash, setSaleFlash] = useState(false);
  const playKaching = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      [[1318.5, 0], [1568, 0.09], [2093, 0.18]].forEach(([f, t]) => {
        const o = ctx.createOscillator(); const g = ctx.createGain();
        o.type = "triangle"; o.frequency.value = f;
        g.gain.setValueAtTime(0.0001, ctx.currentTime + t);
        g.gain.exponentialRampToValueAtTime(0.35, ctx.currentTime + t + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + t + 0.5);
        o.connect(g); g.connect(ctx.destination);
        o.start(ctx.currentTime + t); o.stop(ctx.currentTime + t + 0.55);
      });
      setTimeout(() => { try { ctx.close(); } catch (_) {} }, 1200);
    } catch (_) {}
  };
  const celebrateSale = () => {
    playKaching();
    try { if (navigator.vibrate) navigator.vibrate([120, 60, 200]); } catch (_) {}
    setSaleFlash(true);
    setTimeout(() => setSaleFlash(false), 2200);
  };

  // v4.5.0 — Promo flash.
  const [promoLabel, setPromoLabel] = useState("");
  const [promoPct, setPromoPct] = useState(20);
  const [promoDur, setPromoDur] = useState(10);
  const [promoBusy, setPromoBusy] = useState(false);
  const [promoNow, setPromoNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setPromoNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const startPromo = async () => {
    if (!session?.session_id || !promoLabel.trim()) return;
    setPromoBusy(true);
    try {
      await fetch(`${API}/v3/live/sessions/${session.session_id}/promo`, {
        method: "POST", headers: { ..._auth(), "Content-Type": "application/json" },
        body: JSON.stringify({ label: promoLabel.trim(), discount_pct: promoPct, duration_min: promoDur }),
      });
      fetchStatsFromRef.current && fetchStatsFromRef.current();
    } catch (_) {}
    finally { setPromoBusy(false); }
  };
  const stopPromo = async () => {
    if (!session?.session_id) return;
    setPromoBusy(true);
    try {
      await fetch(`${API}/v3/live/sessions/${session.session_id}/promo`, { method: "DELETE", headers: _auth() });
      fetchStatsFromRef.current && fetchStatsFromRef.current();
    } catch (_) {}
    finally { setPromoBusy(false); }
  };

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await fetch(`${API}/v3/live/sessions/mine`, { headers: _auth() });
      if (r.status === 403) { setError("Cet espace est réservé aux commerçants pros AYA."); setLoading(false); return; }
      const b = await r.json();
      setSession(b.active || null);
    } catch (e) { setError("Impossible de charger la session."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Polling stats + SSE live updates
  useEffect(() => {
    if (!session?.session_id) return;
    const fetchStats = async () => {
      try {
        const r = await fetch(`${API}/v3/live/sessions/${session.session_id}/stats`);
        if (!r.ok) return;
        const b = await r.json();
        // Ka-ching si le compteur de commandes augmente (on ignore le 1er échantillon).
        if (prevOrdersRef.current !== null && (b.stats?.orders_count || 0) > prevOrdersRef.current) {
          celebrateSale();
        }
        prevOrdersRef.current = b.stats?.orders_count || 0;
        setStats(b.stats);
        setRecent(b.recent_orders || []);
      } catch (_) {}
    };
    fetchStats();
    fetchStatsFromRef.current = fetchStats;
    const iv = setInterval(fetchStats, 5000);
    // SSE
    const es = new EventSource(`${API}/v3/live/sessions/${session.session_id}/stream`);
    esRef.current = es;
    es.onmessage = (ev) => {
      try {
        const d = JSON.parse(ev.data);
        if (d.type === "order_placed" || d.type === "viewer_scanned" || d.type === "order_dispatch_updated") fetchStats();
      } catch (_) {}
    };
    return () => { clearInterval(iv); try { es.close(); } catch (_) {} };
  }, [session?.session_id]);

  const start = async () => {
    setStarting(true); setError(null);
    try {
      const r = await fetch(`${API}/v3/live/sessions`, { method: "POST", headers: { ..._auth(), "Content-Type": "application/json" }, body: JSON.stringify({ platform, live_url: liveUrl || null, live_title: liveTitle || null }) });
      const b = await r.json();
      if (!r.ok || !b.ok) throw new Error(b.error || "start_failed");
      setSession(b.session);
    } catch (e) { setError("Impossible de démarrer le live."); }
    finally { setStarting(false); }
  };

  const close = async () => {
    if (!session?.session_id) return;
    if (!window.confirm("Terminer le live ?")) return;
    const sid = session.session_id;
    await fetch(`${API}/v3/live/sessions/${sid}/close`, { method: "POST", headers: _auth() });
    // v3.0.0-rc.8v — Récupère le debrief IA après clôture.
    try {
      const r = await fetch(`${API}/v3/live/sessions/${sid}/debrief`, { headers: _auth() });
      const b = await r.json();
      if (b.ok) setDebrief(b.debrief);
    } catch (_) {}
    setSession(null); setStats(null); setRecent([]); setProducts([]); setPinnedId(null);
  };

  // v3.0.0-rc.8u — Fetch products of the merchant + pin action.
  const fetchProducts = useCallback(async (sid) => {
    if (!sid) return;
    try {
      const r = await fetch(`${API}/v3/live/sessions/${sid}/products`, { headers: _auth() });
      if (!r.ok) return;
      const b = await r.json();
      setProducts(b.products || []);
      setPinnedId(b.pinned_product_id || null);
    } catch (_) {}
  }, []);
  const pin = async (productId) => {
    if (!session?.session_id) return;
    setPinnedId(productId);
    try {
      await fetch(`${API}/v3/live/sessions/${session.session_id}/pin`, {
        method: "POST", headers: { ..._auth(), "Content-Type": "application/json" },
        body: JSON.stringify({ product_id: productId }),
      });
    } catch (_) {}
  };
  useEffect(() => { if (session?.session_id) fetchProducts(session.session_id); }, [session?.session_id, fetchProducts]);

  const qrUrl = useMemo(() => session ? `${API}/v3/live/sessions/${session.session_id}/qr.png` : null, [session?.session_id]);
  // v4.4.0 — Bannière overlay premium (page React à ajouter en source navigateur OBS).
  const overlayPageUrl = useMemo(() => session ? `${window.location.origin}/live/overlay/${session.session_id}` : null, [session?.session_id]);
  const copyOverlayLink = async () => {
    if (!overlayPageUrl) return;
    try { await navigator.clipboard.writeText(overlayPageUrl); alert("Lien overlay copié ! Ajoute-le comme source navigateur dans OBS."); } catch (_) {}
  };

  // v3.8.0 — Fichier overlay autonome (.html) : à importer dans OBS / Streamlabs /
  // TikTok Live Studio. Iframe transparente vers l'overlay temps réel (produit épinglé inclus).
  const downloadOverlayFile = () => {
    if (!overlayPageUrl || !session) return;
    const html = `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="utf-8" />
<title>AYA Live Overlay — ${(session.live_title || "Mon live").replace(/</g, "")}</title>
<style>html,body{margin:0;padding:0;height:100%;background:transparent;overflow:hidden}iframe{border:0;width:100vw;height:100vh;background:transparent}</style>
</head>
<body>
<!-- Overlay AYA Live : boutique, produit épinglé, promo flash et QR d'achat en temps réel. -->
<iframe src="${overlayPageUrl}" allowtransparency="true"></iframe>
</body>
</html>`;
    const blob = new Blob([html], { type: "text/html" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `aya-live-overlay-${session.session_id.slice(0, 8)}.html`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 3000);
  };

  const share = async () => {
    if (!session?.landing_url) return;
    try {
      if (navigator.share) await navigator.share({ title: "Ma boutique AYA en live", url: session.landing_url });
      else { await navigator.clipboard.writeText(session.landing_url); alert("Lien copié !"); }
    } catch (_) {}
  };

  const download = () => { if (qrUrl) window.open(qrUrl, "_blank"); };

  if (loading) return <div style={_shell}><div style={_wrap}><div style={_center}>Chargement…</div></div></div>;
  if (error) return (
    <div style={_shell}>
      <div style={_wrap}>
        <div style={_center}>
          <p style={{ opacity: 0.8 }}>{error}</p>
          <button onClick={() => navigate("/mon-compte")} style={_btnGhost}>← Retour espace pro</button>
        </div>
      </div>
    </div>
  );

  return (
    <div style={_shell}>
    <div style={_wrap} data-testid="live-dashboard-root">
      <div style={_header}>
        <button onClick={() => navigate("/mon-compte")} style={_btnGhost} data-testid="live-back">← Mon compte</button>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, letterSpacing: -0.3 }}>🎥 AYA Live</h1>
        <div style={{ width: 90 }} />
      </div>

      {debrief && !session && (
        <section data-testid="live-debrief-card" style={{ ..._card, borderColor: "#EBD9A8", background: "linear-gradient(180deg, rgba(11,69,49,0.6), rgba(4,24,15,0.6))" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: "#EBD9A8" }}>🧠 Debrief AYA — Live de {debrief.duration_min} min</h3>
            <button data-testid="live-debrief-close" onClick={() => setDebrief(null)} style={{ ..._btnGhost, padding: "4px 10px" }}>×</button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 8, marginBottom: 10 }}>
            <StatMini label="CA total" value={`${fmtFcfa(debrief.gmv_fcfa)} F`} />
            <StatMini label="Ventes" value={debrief.orders_count} />
            <StatMini label="Scans" value={debrief.scans_count} />
            <StatMini label="Panier moyen" value={`${fmtFcfa(debrief.panier_moyen_fcfa)} F`} small />
            <StatMini label="Conversion" value={`${debrief.conversion_rate_pct}%`} />
            {debrief.peak_hour && <StatMini label="Heure de pic" value={debrief.peak_hour} />}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {(debrief.insights || []).map((line, i) => (
              <div key={i} data-testid={`live-debrief-insight-${i}`} style={{ fontSize: 13, lineHeight: 1.5, padding: "8px 10px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(232,213,163,0.15)", borderRadius: 10 }}
                dangerouslySetInnerHTML={{ __html: line.replace(/\*\*(.+?)\*\*/g, '<strong style="color:#EBD9A8">$1</strong>') }}
              />
            ))}
          </div>
        </section>
      )}

      {!session ? (
        <div style={{ ..._card, textAlign: "center", padding: "32px 20px" }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>📡</div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>Prêt à passer en live ?</h2>
          <p style={{ opacity: 0.7, fontSize: 12.5, marginTop: 6, lineHeight: 1.5 }}>
            Génère ton QR AYA et colle-le en surimpression sur ton live.<br/>
            <span style={{ opacity: 0.6 }}>AYA ne regarde pas ton flux vidéo — elle observe uniquement les scans, achats et clics de son côté.</span>
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 16, textAlign: "left" }}>
            <div>
              <label style={{ fontSize: 11, opacity: 0.7, textTransform: "uppercase", letterSpacing: 0.4 }}>Plateforme</label>
              <div style={{ display: "flex", gap: 6, marginTop: 4, flexWrap: "wrap" }}>
                {[["tiktok","🎵 TikTok"],["instagram","📸 Instagram"],["facebook","📘 Facebook"],["snap","👻 Snap"],["other","🌐 Autre"]].map(([id, label]) => (
                  <button key={id} type="button" onClick={() => setPlatform(id)} data-testid={`live-platform-${id}`}
                    style={{ padding: "6px 12px", borderRadius: 999, cursor: "pointer",
                      border: platform === id ? "1.5px solid #EBD9A8" : "1px solid rgba(255,255,255,0.14)",
                      background: platform === id ? "rgba(232,213,163,0.14)" : "rgba(255,255,255,0.04)",
                      color: "inherit", fontSize: 12, fontWeight: 700,
                    }}>{label}</button>
                ))}
              </div>
            </div>
            <label style={{ fontSize: 11, opacity: 0.7, textTransform: "uppercase", letterSpacing: 0.4 }}>URL du live <span style={{ opacity: 0.5, textTransform: "none", letterSpacing: 0 }}>(optionnel)</span></label>
            <input data-testid="live-url-input" value={liveUrl} onChange={(e) => setLiveUrl(e.target.value)} placeholder="https://tiktok.com/@toi/live"
              style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.14)", color: "#F4F1E8", borderRadius: 10, padding: "10px 12px", fontSize: 13 }} />
            <label style={{ fontSize: 11, opacity: 0.7, textTransform: "uppercase", letterSpacing: 0.4 }}>Titre du live <span style={{ opacity: 0.5, textTransform: "none", letterSpacing: 0 }}>(optionnel)</span></label>
            <input data-testid="live-title-input" value={liveTitle} onChange={(e) => setLiveTitle(e.target.value)} placeholder="Ex : Déstockage robes wax jusqu'à -40%"
              style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.14)", color: "#F4F1E8", borderRadius: 10, padding: "10px 12px", fontSize: 13 }} />
          </div>
          <button onClick={start} disabled={starting} style={_btnPrimary} data-testid="live-start-btn">
            {starting ? "Démarrage…" : "🔴 Démarrer le live"}
          </button>
        </div>
      ) : (
        <>
          {/* ── 1. Barre de statut live ─────────────────────────────────── */}
          <section style={{ ..._card, padding: "14px 18px" }} data-testid="live-active-card">
            <div style={_liveHead}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <div style={_liveBadge}><span style={_dotPulse} />&nbsp;LIVE EN COURS <span style={_enDirect}>EN DIRECT</span></div>
                <span style={{ opacity: 0.7, fontSize: 12, fontFeatureSettings: "'tnum'" }}>⏱ {fmtDuration(stats?.duration_s)}</span>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button style={_iconBtn} onClick={share} data-testid="live-share">↗</button>
                <button style={{ ..._btnGhost, color: "#EF4444", borderColor: "rgba(239,68,68,0.4)" }} onClick={close} data-testid="live-close">Terminer</button>
              </div>
            </div>
          </section>

          {/* ── 2. CA hero ──────────────────────────────────────────────── */}
          <section style={{
            ..._card, textAlign: "center", padding: "18px 18px 16px", position: "relative",
            transition: "box-shadow 300ms ease, border-color 300ms ease",
            ...(saleFlash ? { boxShadow: "0 0 0 2px rgba(16,185,129,0.8), 0 0 32px rgba(16,185,129,0.45)" } : {}),
          }} data-testid="live-ca-card">
            {saleFlash && (
              <div data-testid="live-sale-flash" style={{
                position: "absolute", top: 10, right: 12, background: "#10B981", color: "#04150D",
                fontSize: 12, fontWeight: 900, padding: "5px 12px", borderRadius: 999,
                animation: "aya-kaching-pop 0.4s ease",
              }}>💰 Nouvelle vente !</div>
            )}
            <style>{`@keyframes aya-kaching-pop { 0% { transform: scale(0.4); opacity: 0; } 70% { transform: scale(1.15); } 100% { transform: scale(1); opacity: 1; } }`}</style>
            <div style={{ fontSize: 11, opacity: 0.65, textTransform: "uppercase", letterSpacing: 0.6 }}>Chiffre d&apos;affaires en direct</div>
            <div style={{ fontSize: 40, fontWeight: 900, color: "#10B981", letterSpacing: -1, fontFeatureSettings: "'tnum'", marginTop: 4 }}>
              {fmtFcfa(stats?.gmv_fcfa)} <span style={{ fontSize: 16 }}>FCFA</span>
            </div>
          </section>

          {/* ── 3. KPIs alignés ─────────────────────────────────────────── */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 8, marginBottom: 14 }} data-testid="live-kpi-grid">
            <StatMini label="Vues live" value={stats?.viewers_count || 0} />
            <StatMini label="Scans QR" value={stats?.scans_count || 0} />
            <StatMini label="Commandes" value={stats?.orders_count || 0} />
            <StatMini label="Panier moyen" value={`${fmtFcfa(stats?.panier_moyen)} F`} small />
          </div>

          {/* ── 4. QR & Overlay ─────────────────────────────────────────── */}
          <section style={_card} data-testid="live-qr-overlay-card">
            <h3 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 800 }}>📱 QR & Overlay du live</h3>
            <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
              {qrUrl && <img src={qrUrl} alt="QR AYA Live" width={150} height={150} style={{ borderRadius: 12, border: "2px solid rgba(232,213,163,0.35)", background: "#fff", flexShrink: 0 }} data-testid="live-qr-img" />}
              <div style={{ flex: 1, minWidth: 220, display: "flex", flexDirection: "column", gap: 8 }}>
                <div style={{ fontSize: 12, opacity: 0.7, lineHeight: 1.5 }}>
                  Affiche le QR pendant ton live, ou ajoute la <strong style={{ color: "#EBD9A8" }}>bannière overlay premium</strong> en surimpression
                  (source navigateur OBS / app de stream) : logo AYA, ta boutique, ta note et le QR « Scannez pour acheter ».
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button onClick={download} style={_btnSm} data-testid="live-download-qr">⬇ QR</button>
                  <button onClick={() => window.open(overlayPageUrl, "_blank")} style={{ ..._btnSm, background: "linear-gradient(135deg, rgba(245,233,198,0.25), rgba(205,164,82,0.18))" }} data-testid="live-open-overlay">🎨 Overlay Live</button>
                  <button onClick={copyOverlayLink} style={_btnSm} data-testid="live-copy-overlay">📋 Copier lien OBS</button>
                  <button onClick={downloadOverlayFile} style={_btnSm} data-testid="live-download-overlay">⬇ Fichier overlay (.html)</button>
                  <button onClick={share} style={_btnSm} data-testid="live-share-qr">↗ Partager</button>
                </div>
              </div>
            </div>
          </section>

          {/* ── 4bis. Promo flash ───────────────────────────────────────── */}
          <section style={_card} data-testid="live-promo-card">
            <h3 style={{ margin: "0 0 10px", fontSize: 15, fontWeight: 800 }}>⚡ Promo flash du live</h3>
            {stats?.flash_promo && new Date(stats.flash_promo.ends_at).getTime() > promoNow ? (
              <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }} data-testid="live-promo-active">
                <div style={{ flex: 1, minWidth: 180 }}>
                  <div style={{ fontSize: 14, fontWeight: 900, color: "#EBD9A8" }}>
                    {stats.flash_promo.label} · −{stats.flash_promo.discount_pct}%
                  </div>
                  <div style={{ fontSize: 11, opacity: 0.6, marginTop: 2 }}>Affichée sur l&apos;overlay avec compte à rebours</div>
                </div>
                <div style={{ fontSize: 24, fontWeight: 900, fontFeatureSettings: "'tnum'", color: "#EBD9A8" }} data-testid="live-promo-countdown">
                  {(() => { const left = Math.max(0, new Date(stats.flash_promo.ends_at).getTime() - promoNow); return `${String(Math.floor(left / 60000)).padStart(2, "0")}:${String(Math.floor((left % 60000) / 1000)).padStart(2, "0")}`; })()}
                </div>
                <button onClick={stopPromo} disabled={promoBusy} style={{ ..._btnGhost, color: "#EF4444", borderColor: "rgba(239,68,68,0.4)" }} data-testid="live-promo-stop">Arrêter</button>
              </div>
            ) : (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                <input
                  value={promoLabel}
                  onChange={(e) => setPromoLabel(e.target.value)}
                  placeholder="Ex : PAGNE10, Sacs -30%…"
                  maxLength={40}
                  data-testid="live-promo-label-input"
                  style={{ flex: 1, minWidth: 150, padding: "10px 12px", borderRadius: 10, border: "1px solid rgba(232,213,163,0.3)", background: "rgba(255,255,255,0.05)", color: "inherit", fontSize: 13, outline: "none" }}
                />
                <select value={promoPct} onChange={(e) => setPromoPct(Number(e.target.value))} data-testid="live-promo-pct" style={_selectSm}>
                  {[10, 20, 30, 40, 50].map((p) => <option key={p} value={p}>−{p}%</option>)}
                </select>
                <select value={promoDur} onChange={(e) => setPromoDur(Number(e.target.value))} data-testid="live-promo-dur" style={_selectSm}>
                  {[5, 10, 15, 30, 60].map((d) => <option key={d} value={d}>{d} min</option>)}
                </select>
                <button onClick={startPromo} disabled={promoBusy || !promoLabel.trim()} style={{ ..._btnSm, opacity: promoLabel.trim() ? 1 : 0.5 }} data-testid="live-promo-start">⚡ Lancer</button>
              </div>
            )}
          </section>

          {/* v3.0.0-rc.8u — Product Pinning : le commerçant sélectionne le produit qu'il montre à la caméra. */}
          <section style={_card} data-testid="live-pin-picker">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800 }}>📌 Épingle le produit que tu montres</h3>
              <div style={{ fontSize: 11, opacity: 0.55 }}>Instantané sur le landing scan</div>
            </div>
            {products.length === 0 ? (
              <div style={{ padding: "18px 8px", textAlign: "center", opacity: 0.55, fontSize: 12.5 }}>
                Aucun produit dans ta boutique — ajoute-en depuis Console Partenaires.
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 8 }}>
                {products.map((p) => {
                  const active = pinnedId === p.product_id;
                  return (
                    <button
                      key={p.product_id}
                      onClick={() => pin(p.product_id)}
                      data-testid={`live-pin-product-${p.product_id}`}
                      disabled={!p.available}
                      style={{
                        textAlign: "left", cursor: p.available ? "pointer" : "default",
                        background: active ? "linear-gradient(135deg, rgba(232,213,163,0.35), rgba(205,164,82,0.2))" : "rgba(255,255,255,0.04)",
                        border: active ? "2px solid #EBD9A8" : "1px solid rgba(255,255,255,0.10)",
                        borderRadius: 12,
                        padding: 8,
                        color: "inherit",
                        opacity: p.available ? 1 : 0.4,
                        display: "flex", flexDirection: "column", gap: 4,
                        position: "relative",
                      }}
                    >
                      {active && (
                        <span style={{ position: "absolute", top: 4, right: 4, background: "#EBD9A8", color: "#3A2E10", fontSize: 9, fontWeight: 800, padding: "2px 6px", borderRadius: 999, textTransform: "uppercase", letterSpacing: 0.4 }}>épinglé</span>
                      )}
                      {(p.photos && p.photos[0]) ? (
                        <img src={p.photos[0]} alt={p.name} style={{ width: "100%", aspectRatio: "1", objectFit: "cover", borderRadius: 8, background: "#02120A" }} />
                      ) : (
                        <div style={{ width: "100%", aspectRatio: "1", background: "#02120A", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 34 }}>{p.emoji || "🛍"}</div>
                      )}
                      <div style={{ fontSize: 12, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</div>
                      <div style={{ fontSize: 11, color: "#EBD9A8", fontWeight: 800 }}>{fmtFcfa(p.price_fcfa)} FCFA</div>
                    </button>
                  );
                })}
              </div>
            )}
          </section>

          <section style={_card}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800 }}>Achats en direct <span style={{ background: "#EF4444", color: "#fff", borderRadius: 999, padding: "2px 8px", fontSize: 11, marginLeft: 6 }}>{recent.length}</span></h3>
              <div style={{ fontSize: 11, opacity: 0.55 }}>● Actualisé en temps réel</div>
            </div>
            {recent.length === 0 ? (
              <div style={{ padding: "24px 8px", textAlign: "center", opacity: 0.55, fontSize: 13 }}>Aucun achat pour l&apos;instant — partage ton QR ! 🚀</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {recent.map((o) => (
                  <LiveOrderRow
                    key={o.meta?.order_id || Math.random()}
                    order={o}
                    sessionId={session.session_id}
                    auth={_auth}
                    onDispatchDone={() => fetchStatsFromRef.current && fetchStatsFromRef.current()}
                  />
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
    </div>
  );
}

function StatMini({ label, value, small }) {
  return (
    <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: "8px 10px" }}>
      <div style={{ fontSize: 10, opacity: 0.65, textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: small ? 14 : 18, fontWeight: 800, marginTop: 2 }}>{value}</div>
    </div>
  );
}

// v3.7.0 — Row d'un achat live : bouton unique à 3 états
// "✅ Accepter la commande" → "🛵 Faire livrer" → "✓ Livré" (+ preuve photo).
function LiveOrderRow({ order, sessionId, auth, onDispatchDone }) {
  const [busy, setBusy] = useState(false);
  const [proofOpen, setProofOpen] = useState(false);
  const meta = order.meta || {};
  const orderId = meta.order_id;
  const shortId = (orderId || "").slice(0, 8);
  const status = meta.order_status || "confirmed";
  const missionState = meta.mission_state;
  const proofPhoto = meta.proof_photo_ref;
  const courierName = meta.resource_first_name;

  const { pill, color, bg, action, btnLabel, done } = statusUx(status, missionState);

  const doAction = async () => {
    if (!orderId || busy || !action) return;
    setBusy(true);
    try {
      const endpoint = action === "accept" ? "accept" : "dispatch";
      const r = await fetch(`${API}/v3/live/sessions/${sessionId}/orders/${orderId}/${endpoint}`, {
        method: "POST", headers: { ...auth(), "Content-Type": "application/json" },
      });
      const b = await r.json();
      if (!r.ok || !b.ok) alert(`Échec : ${b.error || "erreur inconnue"}`);
      onDispatchDone && onDispatchDone();
    } catch (_) { alert("Erreur réseau."); }
    finally { setBusy(false); }
  };

  return (
    <div style={_orderRow} data-testid={`live-order-${shortId}`}>
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 4 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, fontWeight: 700 }}>{meta.buyer_name || "Client"}</span>
          <span data-testid={`live-order-status-${shortId}`} style={{
            fontSize: 10, fontWeight: 800, letterSpacing: 0.4, textTransform: "uppercase",
            padding: "2px 8px", borderRadius: 999, color, background: bg,
          }}>{pill}</span>
          {courierName && (
            <span style={{ fontSize: 10, opacity: 0.7 }}>🛵 {courierName}</span>
          )}
        </div>
        <div style={{ fontSize: 11, opacity: 0.65, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{meta.product_name || "commande live"}</div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
        <div style={{ fontWeight: 800, color: "#EBD9A8", fontFeatureSettings: "'tnum'" }}>{fmtFcfa(meta.amount_fcfa)} FCFA</div>
        {(action || done) && (
          <button
            onClick={doAction}
            disabled={busy || done}
            data-testid={`live-order-action-${shortId}`}
            style={{
              padding: "6px 14px", borderRadius: 999, fontSize: 11, fontWeight: 800,
              border: done ? "1px solid rgba(16,185,129,0.5)" : "none",
              cursor: busy || done ? "default" : "pointer",
              background: done
                ? "rgba(16,185,129,0.15)"
                : action === "accept"
                  ? "linear-gradient(135deg, #F5E9C6, #CDA452)"
                  : "linear-gradient(135deg, #10B981, #059669)",
              color: done ? "#34D399" : action === "accept" ? "#3A2E10" : "#fff",
              opacity: busy ? 0.6 : 1,
              transition: "background 200ms ease, color 200ms ease",
            }}
          >{busy ? "…" : done ? "✓ Livré" : btnLabel}</button>
        )}
        {proofPhoto && (
          <button
            onClick={() => setProofOpen(true)}
            data-testid={`live-order-proof-${shortId}`}
            style={{
              padding: "4px 10px", borderRadius: 999, fontSize: 10, fontWeight: 700,
              border: "1px solid rgba(232,213,163,0.4)", background: "rgba(232,213,163,0.10)", color: "#EBD9A8",
              cursor: "pointer",
            }}
          >📸 Preuve</button>
        )}
      </div>
      {proofOpen && (
        <div
          onClick={() => setProofOpen(false)}
          data-testid={`live-order-proof-modal-${shortId}`}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)",
            zIndex: 1000, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            padding: 20, cursor: "pointer",
          }}
        >
          <div style={{ fontSize: 13, marginBottom: 10, color: "#EBD9A8" }}>📸 Preuve de livraison · {meta.buyer_name || "Client"}</div>
          <img src={proofPhoto.startsWith("http") || proofPhoto.startsWith("data:") ? proofPhoto : `${(process.env.REACT_APP_BACKEND_URL || "")}${proofPhoto}`} alt="Preuve livraison" style={{ maxWidth: "min(500px, 92vw)", maxHeight: "72vh", borderRadius: 12, border: "2px solid rgba(232,213,163,0.35)" }} />
          <div style={{ fontSize: 11, marginTop: 10, opacity: 0.7 }}>Cliquer pour fermer</div>
        </div>
      )}
    </div>
  );
}

// v3.7.0 — Machine à états du bouton unique : accept → dispatch → livré.
function statusUx(orderStatus, missionState) {
  if (missionState === "completed" || orderStatus === "delivered") {
    return { pill: "✓ Livré", color: "#ECFDF5", bg: "rgba(16,185,129,0.55)", action: null, done: true };
  }
  if (missionState === "proof_pending") {
    return { pill: "📸 En attente preuve", color: "#7A5C10", bg: "rgba(232,213,163,0.25)", action: null };
  }
  if (missionState === "en_route") {
    return { pill: "🛵 En cours de livraison", color: "#1E40AF", bg: "rgba(96,165,250,0.28)", action: null };
  }
  if (missionState === "picked_up" || missionState === "pickup_arrived") {
    return { pill: "📦 Colis récupéré", color: "#1E40AF", bg: "rgba(96,165,250,0.22)", action: null };
  }
  if (missionState === "assigned" || orderStatus === "dispatched" || orderStatus === "courier_assigned") {
    return { pill: "🛵 Livreur en route", color: "#1E40AF", bg: "rgba(96,165,250,0.18)", action: null };
  }
  if (orderStatus === "dispatch_pending" || orderStatus === "broadcasting") {
    return { pill: "📡 Recherche livreur…", color: "#7A5C10", bg: "rgba(232,213,163,0.20)", action: null };
  }
  if (missionState === "cancelled" || missionState === "disputed" || orderStatus === "dispatch_expired") {
    return { pill: "⚠ Problème", color: "#7F1D1D", bg: "rgba(239,68,68,0.22)", action: "dispatch", btnLabel: "🛵 Relancer livreur" };
  }
  if (orderStatus === "merchant_accepted") {
    return { pill: "🛍️ Acceptée", color: "#3A2E10", bg: "rgba(232,213,163,0.35)", action: "dispatch", btnLabel: "🛵 Faire livrer" };
  }
  // Cas par défaut : confirmed (escrow HELD) → le commerçant doit accepter.
  return { pill: "🆕 Nouvelle commande", color: "#ECFDF5", bg: "rgba(16,185,129,0.35)", action: "accept", btnLabel: "✅ Accepter la commande" };
}

const _wrap = { maxWidth: 720, margin: "0 auto", padding: "20px 16px 100px", color: "#F4F1E8", minHeight: "100vh" };
// v3.0.0-rc.8x — Shell full-viewport en vert foncé profond (référence design AYA Live originale).
const _shell = { background: "#02120A", minHeight: "100vh", width: "100%" };
const _header = { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 };
const _card = { background: "linear-gradient(180deg, rgba(6,42,31,0.5) 0%, rgba(4,24,15,0.5) 100%)", border: "1px solid rgba(232,213,163,0.18)", borderRadius: 18, padding: 18, marginBottom: 14, boxShadow: "0 6px 20px rgba(0,0,0,0.25)" };
const _liveHead = { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 };
const _liveBadge = { display: "flex", alignItems: "center", fontSize: 12, fontWeight: 800, letterSpacing: 0.4, textTransform: "uppercase" };
const _dotPulse = { width: 9, height: 9, borderRadius: "50%", background: "#10B981", marginRight: 6, boxShadow: "0 0 0 4px rgba(16,185,129,0.25)", animation: "pulse 1.6s infinite" };
const _enDirect = { marginLeft: 8, background: "#EF4444", color: "#fff", padding: "2px 8px", borderRadius: 999, fontSize: 10 };
const _btnPrimary = { marginTop: 18, padding: "12px 24px", borderRadius: 999, background: "linear-gradient(135deg, #F5E9C6, #E4C87F 55%, #CDA452)", color: "#3A2E10", border: "none", fontSize: 14, fontWeight: 800, cursor: "pointer" };
const _btnGhost = { background: "transparent", color: "#EBD9A8", border: "1px solid rgba(232,213,163,0.35)", borderRadius: 999, padding: "6px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer" };
const _btnSm = { padding: "6px 12px", borderRadius: 999, background: "rgba(232,213,163,0.10)", color: "#EBD9A8", border: "1px solid rgba(232,213,163,0.35)", fontSize: 11, fontWeight: 700, cursor: "pointer" };
const _selectSm = { padding: "9px 10px", borderRadius: 10, background: "rgba(255,255,255,0.05)", color: "inherit", border: "1px solid rgba(232,213,163,0.3)", fontSize: 12.5, fontWeight: 700, cursor: "pointer" };
const _iconBtn = { width: 32, height: 32, borderRadius: 8, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.14)", color: "#F4F1E8", cursor: "pointer", fontSize: 16 };
const _orderRow = { display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12 };
const _center = { textAlign: "center", padding: 30 };
